import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { getNavItems } from "@/config/nav-config";
import { useLayout } from "@/contexts/layout-context";
import { triggerHaptic } from "@/lib/haptic-feedback";
import { cn } from "@/lib/utils";
import type { Data } from "@/types/data";
import { Menu } from "lucide-react";
import { useMemo, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../public/logo/logo.avif";
import NavItem from "./nav-item";

interface HeaderProps {
	data: Data;
}

export default function Header({ data }: HeaderProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const [menuOpen, setMenuOpen] = useState(false);
	const navItems = getNavItems(data);
	const { selectedLayout, setSelectedLayout, isShowcaseMode } = useLayout();

	const layoutToUse = isShowcaseMode ? selectedLayout : Number(data.layout);

	const whatsappLink = useMemo(
		() =>
			data.cliente["redes-sociais"].find((item) =>
				item.nome.toLowerCase().includes("whatsapp"),
			)?.url,
		[data],
	);

	const handleLayoutChange = (layout: number) => {
		triggerHaptic("medium");
		setSelectedLayout(layout);
		navigate("/");
		setMenuOpen(false);
	};

	const layoutLabel = (layout: number) => {
		if (layout === 1) return "Paginado";
		if (layout === 2) return "Contínuo";
		return String(layout);
	};

	const getNavLinkClasses = (href: string) => {
		const currentPath = location.pathname.startsWith("/")
			? location.pathname.slice(1)
			: location.pathname;
		const currentHash = location.hash.startsWith("#")
			? location.hash.slice(1)
			: location.hash;

		const isActive =
			layoutToUse === 2
				? href === ""
					? currentHash === ""
					: currentHash === href
				: currentPath === href;

		return cn(
			"rounded-full px-3.5 py-2 text-sm font-medium transition-all duration-200",
			isActive
				? "bg-brand-600 text-white shadow-sm"
				: "text-zinc-700 hover:bg-zinc-200",
		);
	};

	const LayoutSelector = () => {
		if (!isShowcaseMode) return null;

		return (
			<div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-100 p-1">
				{[1, 2].map((layout) => (
					<button
						type="button"
						key={layout}
						onClick={() => handleLayoutChange(layout)}
						className={cn(
							"rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 active:scale-[0.97]",
							selectedLayout === layout
								? "bg-brand-600 text-white"
								: "text-zinc-700 hover:bg-white",
						)}
					>
						{layoutLabel(layout)}
					</button>
				))}
			</div>
		);
	};

	return (
		<header className="fixed inset-x-0 top-0 z-50 border-b border-zinc-200/80 bg-white backdrop-blur supports-[backdrop-filter]:bg-white/85">
			<nav className="mx-auto flex h-20 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
				<Link
					to="/"
					className="shrink-0"
					onClick={() => triggerHaptic("light")}
				>
					<img src={logo} alt="Logo do laboratório" className="h-11 md:h-14" width="195" height="56" />
				</Link>

				<div className="hidden xl:ml-auto xl:flex xl:items-center xl:gap-3">
					<ul className="flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-100/80 p-1">
						{navItems
							.filter((item) => item.enabled !== false)
							.map((navItem) => (
								<li
									key={navItem.label}
									className={getNavLinkClasses(navItem.href)}
								>
									<NavItem
										data={data}
										label={navItem.label}
										href={navItem.href}
										className="block w-full"
									/>
								</li>
							))}
					</ul>

					{whatsappLink && (
						<a
							href={whatsappLink}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() => triggerHaptic("light")}
							className="inline-flex h-10 items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:bg-emerald-100 hover:text-zinc-900"
						>
							<FaWhatsapp className="h-4 w-4 text-emerald-500" />
							Fale conosco
						</a>
					)}

					<LayoutSelector />
				</div>

				<div className="ml-auto xl:hidden">
					<Popover open={menuOpen} onOpenChange={setMenuOpen}>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-700 transition-colors hover:bg-zinc-100"
								aria-label="Abrir menu"
							>
								<Menu className="h-5 w-5" />
							</button>
						</PopoverTrigger>

						<PopoverContent align="end" className="w-72 border-zinc-200 p-3">
							{isShowcaseMode && (
								<div className="mb-3 border-b border-zinc-200 pb-3">
									<span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
										Layout
									</span>
									<div className="grid grid-cols-2 gap-2">
										{[1, 2].map((layout) => (
											<button
												key={layout}
												type="button"
												onClick={() => handleLayoutChange(layout)}
												className={cn(
													"rounded-md px-2 py-2 text-sm font-semibold transition-colors",
													selectedLayout === layout
														? "bg-brand-600 text-white"
														: "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
												)}
											>
												{layoutLabel(layout)}
											</button>
										))}
									</div>
								</div>
							)}

							<ul className="space-y-1">
								{navItems
									.filter((item) => item.enabled !== false)
									.map((navItem) => (
										<li
											key={navItem.label}
											className={cn(
												getNavLinkClasses(navItem.href),
												"w-full rounded-md",
											)}
										>
											<NavItem
												data={data}
												label={navItem.label}
												href={navItem.href}
												className="block w-full"
												onClick={() => setMenuOpen(false)}
											/>
										</li>
									))}
							</ul>

							{whatsappLink && (
								<a
									href={whatsappLink}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										triggerHaptic("light");
										setMenuOpen(false);
									}}
									className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-zinc-200 bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-200"
								>
									<FaWhatsapp className="h-4 w-4 text-emerald-500" />
									Falar no WhatsApp
								</a>
							)}
						</PopoverContent>
					</Popover>
				</div>
			</nav>
		</header>
	);
}
